import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import logo from "../../assets/meta/logo.png";
import { useNavigate, useParams } from "react-router-dom";

/* ================= THEME ================= */
const COMMERCE_GRADIENT =
  "linear-gradient(135deg, #0A66C2 0%, #000000 100%)";

const MetaCommerceCheckoutSuccess: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  /* Auto-redirect after 60 seconds */
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/");
    }, 60000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: COMMERCE_GRADIENT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box
        sx={{
          background: "#fff",
          borderRadius: 5,
          p: { xs: 4, md: 6 },
          maxWidth: 480,
          width: "100%",
          textAlign: "center",
          boxShadow: "0 30px 90px rgba(0,0,0,.45)",
        }}
      >
        {/* LOGO CIRCLE */}
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{
            width: 160,
            height: 160,
            margin: "0 auto",
            borderRadius: "50%",
            background: COMMERCE_GRADIENT,
            padding: 6,
            boxShadow:
              "0 20px 50px rgba(0,0,0,.45), inset 0 4px 12px rgba(255,255,255,.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              boxShadow: "inset 0 6px 18px rgba(0,0,0,.15)",
            }}
          >
            <img
              src={logo}
              alt="Armerix"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        </motion.div>

        {/* SUCCESS ICON */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            delay: 0.4,
            type: "spring",
            stiffness: 260,
            damping: 16,
          }}
          style={{ marginTop: -28 }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "#16a34a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              boxShadow: "0 12px 30px rgba(22,163,74,.5)",
            }}
          >
            <CheckCircleIcon sx={{ color: "#fff", fontSize: 32 }} />
          </Box>
        </motion.div>

        {/* TITLE */}
        <Typography
          sx={{
            mt: 4,
            fontWeight: 900,
            fontSize: 24,
            color: "#000",
          }}
        >
          Order Confirmed
        </Typography>

        {/* DESCRIPTION */}
        <Typography
          sx={{
            mt: 1.5,
            fontSize: 15,
            color: "#555",
            lineHeight: 1.6,
          }}
        >
          Thank you for shopping with <strong>Armerix</strong>.
          <br />
          Your order has been successfully placed and is now being processed.
        </Typography>

        {/* OPTIONAL REFERENCE */}
        {userId && (
          <Typography
            sx={{
              mt: 2,
              fontSize: 12,
              color: "#aaa",
            }}
          >
            Reference: {userId}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MetaCommerceCheckoutSuccess;
